import { useCallback, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { Dropdown } from "primereact/dropdown";
import { useMountEffect } from "primereact/hooks";
import { getAuditLogPeople } from "../../../services/AuditLogService";
import { useServerSearch } from "../../common/hooks/useServerSearch";
import { AUDIT_SYSTEM_USER_ID } from "../../../constants/AuditLogConstants";

// The Anyone filter: people found on the server as the admin types (name, email
// or username, deleted people too), System for the background jobs, and the
// chosen person even when a search leaves them out. The server decides which
// people match (it also looks at usernames the label does not show), so every
// option carries the typed text in `match` and the dropdown's own filter, which
// looks at that field, never hides one.
// The Anyone choice's own value: an empty value would show no label at all.
const ANYONE = "__anyone__";

const AuditLogPeoplePicker = ({ value, name, onChange }) => {
  const intl = useIntl();
  const [people, setPeople] = useState([]);
  const [typed, setTyped] = useState("");
  const latestRequest = useRef(0);

  const load = useCallback(async (search = "") => {
    const request = ++latestRequest.current;
    try {
      const ajax = await getAuditLogPeople(search);
      // A slower, older answer must not replace the newer one
      if (request === latestRequest.current) {
        setPeople(ajax?.data?.response || []);
      }
    } catch (error) {
      if (request === latestRequest.current) {
        setPeople([]);
      }
    }
  }, []);

  const onFilter = useServerSearch(load);

  useMountEffect(() => {
    load("");
  });

  const options = useMemo(() => {
    const system = intl.formatMessage({ id: "audit_system" });
    const found = people.map((person) => ({
      label: person.email ? `${person.name} (${person.email})` : person.name,
      value: person.id,
      name: person.name,
      match: typed,
    }));
    const chosen =
      value && value !== AUDIT_SYSTEM_USER_ID && !found.some((option) => option.value === value)
        ? [{ label: name || value, value, name, match: typed }]
        : [];
    return [
      { label: intl.formatMessage({ id: "audit_anyone" }), value: ANYONE, match: typed },
      { label: system, value: AUDIT_SYSTEM_USER_ID, name: system, match: typed },
      ...chosen,
      ...found,
    ];
  }, [people, value, name, typed, intl]);

  return (
    <Dropdown
      data-testid="audit-person"
      value={value || ANYONE}
      options={options}
      aria-label={intl.formatMessage({ id: "audit_filter_person" })}
      filter
      filterBy="match"
      emptyMessage={intl.formatMessage({ id: "no_records_found" })}
      emptyFilterMessage={intl.formatMessage({ id: "no_records_found" })}
      onFilter={(event) => {
        setTyped(event.filter || "");
        onFilter(event.filter);
      }}
      filterPlaceholder={intl.formatMessage({ id: "audit_search_people" })}
      onChange={(event) => {
        const option = options.find((item) => item.value === event.value);
        onChange(event.value !== ANYONE ? { id: event.value, name: option?.name } : null);
      }}
    />
  );
};

export default AuditLogPeoplePicker;
